import { lazy, Suspense, useState } from "react";

import { useLocalization } from "../../hooks/useLocalization";
import { useLeaderboard } from "../../hooks/useLeaderboard";

import { LeaderboardTeam } from "../../types/leaderboard";

import { formatLocalizedTimestamp } from "../../utils/leaderboard/format";

import { HistoryChartPoint } from "../../utils/leaderboard/history-chart";

import { DailyMovers } from "./DailyMovers";

import { LeaderboardTabs } from "./LeaderboardTabs";

const HistoryChart = lazy(() =>
  import("./HistoryChart").then((module) => ({ default: module.HistoryChart })),
);

type HomeTab = "leaderboard" | "risers";

interface HomeTabsProps {
  teams?: LeaderboardTeam[];
  standingsLoading?: boolean;
  chartData?: HistoryChartPoint[];
  chartTeams?: string[];
  chartLoading?: boolean;
  teamMetaById?: ReturnType<typeof import("./DailyMovers").buildTeamMetaById>;
  lastUpdated?: string | null;
  live?: boolean;
}

export const HomeTabs = ({
  teams: teamsProp,
  standingsLoading: standingsLoadingProp,
  chartData: chartDataProp,
  chartTeams: chartTeamsProp,
  chartLoading: chartLoadingProp,
  teamMetaById: teamMetaByIdProp,
  lastUpdated: lastUpdatedProp,
  live: liveProp,
}: HomeTabsProps = {}) => {
  const { strings, locale } = useLocalization();
  const context = useLeaderboard();

  const teams = teamsProp ?? context.teams;
  const standingsLoading = standingsLoadingProp ?? context.standingsLoading;
  const chartData = chartDataProp ?? context.chartData;
  const chartTeams = chartTeamsProp ?? context.chartTeams;
  const chartLoading = chartLoadingProp ?? context.chartLoading;
  const teamMetaById = teamMetaByIdProp ?? context.teamMetaById;
  const lastUpdated = lastUpdatedProp ?? context.lastUpdated;
  const live = liveProp ?? context.live;

  const [tab, setTab] = useState<HomeTab>("leaderboard");
  const [leaderboardView, setLeaderboardView] = useState<"teams" | "players">("players");

  const tabs: { id: HomeTab; label: string }[] = [
    { id: "leaderboard", label: strings.LEADERBOARD.TAB_LEADERBOARD },
    { id: "risers", label: strings.LEADERBOARD.TAB_RISERS },
  ];

  return (
    <div className="pb-16 pt-10">
      <section className="mb-8 flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="m-0 font-morganite text-[clamp(2.8rem,7vw,4.5rem)] leading-[0.95] tracking-[0.04em]">
            {strings.LEADERBOARD.PAGE_TITLE}
          </h1>

          {tab === "risers" ? (
            <p className="mb-0 mt-2 max-w-[42rem] text-[#8b8b9a]">
              {strings.LEADERBOARD.RISERS_DESCRIPTION}
            </p>
          ) : null}
        </div>

        <div className="self-end text-[0.95rem] text-[#8b8b9a]">
          {tab === "leaderboard" ? (
            live ? (
              <>
                {strings.LEADERBOARD.LIVE_RANKINGS}

                <br />

                {lastUpdated
                  ? strings.LEADERBOARD.RANKS_REFRESHED.replace(
                      "{time}",
                      formatLocalizedTimestamp(lastUpdated, locale, strings),
                    )
                  : strings.LEADERBOARD.SAME_AS_TEAM_PAGES}
              </>
            ) : lastUpdated ? (
              <>
                {strings.LEADERBOARD.LAST_UPDATED}

                <br />

                {formatLocalizedTimestamp(lastUpdated, locale, strings)}
              </>
            ) : (
              <>{strings.LEADERBOARD.NO_RANK_DATA}</>
            )
          ) : null}
        </div>
      </section>

      <div className="mb-5 inline-flex rounded-[10px] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-1">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`fh-ghost-btn rounded-lg px-4 py-2 text-[0.9rem]${
              tab === item.id ? " fh-ghost-btn--active-accent" : ""
            }`}
            onClick={() => {
              setTab(item.id);
            }}
            aria-pressed={tab === item.id}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "leaderboard" ? (
        <>
          <section className="surface-card overflow-hidden max-[900px]:overflow-visible">
            <div className="border-b border-[var(--surface-card-border)] px-5 pb-3 pt-4">
              <h2 className="m-0 text-[0.95rem] font-semibold uppercase tracking-[0.06em] text-[#8b8b9a]">
                {strings.LEADERBOARD.STANDINGS}
              </h2>
            </div>

            <div>
              <LeaderboardTabs
                teams={teams}
                loading={standingsLoading}
                onTabChange={setLeaderboardView}
              />
            </div>
          </section>

          {leaderboardView === "teams" ? (
            <section className="surface-card mt-5 overflow-visible">
              <div className="border-b border-[var(--surface-card-border)] px-5 pb-3 pt-4">
                <h2 className="m-0 text-[0.95rem] font-semibold uppercase tracking-[0.06em] text-[#8b8b9a]">
                  {strings.LEADERBOARD.HISTORY}
                </h2>
              </div>

              <div className="overflow-visible p-5">
                {chartLoading ? (
                  <div className="p-8 text-center text-[#8b8b9a]">
                    {strings.LEADERBOARD.LOADING}
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div className="p-8 text-center text-[#8b8b9a]">
                        {strings.LEADERBOARD.LOADING}
                      </div>
                    }
                  >
                    <HistoryChart data={chartData} teams={chartTeams} />
                  </Suspense>
                )}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <DailyMovers teamMetaById={teamMetaById} />
      )}
    </div>
  );
};
